// S6 Admin dashboard (root 6.8, REQ-22.1, REQ-22.2): every app's tickets, for every Editor and Admin
// (poc CT-008). Filters and sort in the URL. "!" marks tickets the Platform admins are responsible for.
// Assign to me / Assign / Reassign / Unassign only in Ready and In progress (assigning is the hand-off,
// straight to In progress: poc CT-007); otherwise "Change responsible owner".
// Every command carries `x-feedback-channel: dashboard`. A Type column and Type filter, and statuses of
// both workflows, cover feature requests (poc CT-004). A Linear column (the issue and its Linear status)
// appears when any ticket on the page was handed to Linear; a ticket managed in Linear has no assign
// actions (poc CT-009).
import { CODE_LISTS, type StageCode } from '@feedback/shared/constants/code-lists';
import type { PrincipalSummary } from '@feedback/shared/contracts/identity';
import { type DashboardRow, QUEUE_SORTS } from '@feedback/shared/contracts/query';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { type DataColumn, DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { FilterCheckbox, FilterPanel, FilterSection } from '@/components/FilterPanel';
import { LoadingState } from '@/components/LoadingState';
import { PageHeader } from '@/components/PageHeader';
import { PrincipalName } from '@/components/PrincipalName';
import { PriorityBadge } from '@/components/PriorityBadge';
import { RelativeTime } from '@/components/RelativeTime';
import { StageBadge } from '@/components/StageBadge';
import { TypeBadge } from '@/components/TypeBadge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useAssignToMe } from '@/features/tickets/assign-to-me';
import { AssignDialog, ResponsibleDialog, type TicketTarget } from '@/features/tickets/dialogs';
import { isManagedInLinear, LinearCell } from '@/features/tickets/linear';
import { SORT_LABELS, toggle } from '@/features/tickets/queue-search';
import { StatusOptions } from '@/features/tickets/StatusOptions';
import { api } from '@/lib/api';
import { appQuery, assignableQuery, referenceQuery, useAllStatuses, useStatusGroups } from '@/lib/queries';
import { canApp, canSeeDashboard, requireStaffSession, useSession } from '@/lib/session';
import { useTicketCommand } from '@/lib/ticket-commands';

const PAGE_SIZE = 50;
const KINDS = ['person', 'nobody'] as const;
const KIND_LABELS: Record<(typeof KINDS)[number], string> = {
  person: 'Person',
  nobody: 'Nobody',
};
const ASSIGNABLE_STAGES = new Set<StageCode>(['ready', 'in_progress']);
const OPEN_STAGES = new Set<StageCode>(['triage', 'review', 'ready', 'in_progress']);

interface DashboardSearch {
  appId?: string[];
  stage?: StageCode[];
  status?: string;
  assigneeId?: string;
  assigneeKind?: (typeof KINDS)[number];
  responsibleId?: string;
  /** Ticket type codes: bug, feature_request. */
  type?: string[];
  priority?: string[];
  environment?: string[];
  sort?: (typeof QUEUE_SORTS)[number];
  order?: 'asc' | 'desc';
  page?: number;
}

function list(value: unknown): string[] | undefined {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const out = raw.filter((v): v is string => typeof v === 'string' && v !== '');
  return out.length > 0 ? out : undefined;
}

/** Every key, undefined when missing or invalid, so TanStack's raw search can't keep a bad value (queue-search.ts). */
function parseSearch(search: Record<string, unknown>): DashboardSearch {
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  const stage = list(search.stage)?.filter((s): s is StageCode =>
    (CODE_LISTS.stage_code as readonly string[]).includes(s),
  );
  const page = Number(search.page);
  return {
    appId: list(search.appId),
    stage: stage && stage.length > 0 ? stage : undefined,
    status: str(search.status),
    assigneeId: str(search.assigneeId),
    assigneeKind: KINDS.find((k) => k === search.assigneeKind),
    responsibleId: str(search.responsibleId),
    type: list(search.type),
    priority: list(search.priority),
    environment: list(search.environment),
    sort: QUEUE_SORTS.find((s) => s === search.sort),
    order: search.order === 'asc' || search.order === 'desc' ? search.order : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

export const Route = createFileRoute('/dashboard')({
  validateSearch: parseSearch,
  beforeLoad: async ({ context, location }) => {
    const session = await requireStaffSession(context.queryClient, location.href);
    if (!canSeeDashboard(session)) {
      throw redirect({ to: '/' });
    }
  },
  component: Dashboard,
});

interface DashboardPage {
  items: DashboardRow[];
  page: number;
  pageSize: number;
  total: number;
}

function Dashboard() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = (patch: Partial<DashboardSearch>) =>
    navigate({ search: (prev: DashboardSearch) => ({ ...prev, ...patch, page: undefined }), replace: true });
  const data = useQuery({
    queryKey: ['dashboard', search],
    queryFn: ({ signal }) =>
      api.get<DashboardPage>('/api/dashboard/tickets', { ...search, pageSize: PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
  });
  const activeCount = Object.entries(search).filter(
    ([k, v]) => !['sort', 'order', 'page'].includes(k) && v !== undefined,
  ).length;
  return (
    <>
      <PageHeader title="Dashboard" description="Every ticket in every app, for Editors and Admins." />
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        <DashboardFilters search={search} setSearch={setSearch} activeCount={activeCount} />
        <section aria-label="Tickets" className="flex min-w-0 flex-1 flex-col gap-3">
          <SortBar search={search} setSearch={setSearch} total={data.data?.total} />
          {data.isPending ? (
            <LoadingState label="Loading the dashboard" />
          ) : data.isError ? (
            <ErrorState error={data.error} onRetry={() => void data.refetch()} />
          ) : data.data.items.length === 0 ? (
            <EmptyState title="No tickets match">Try fewer filters.</EmptyState>
          ) : (
            <DashboardTable
              page={data.data}
              isFetching={data.isFetching}
              onPageChange={(page) => navigate({ search: (prev: DashboardSearch) => ({ ...prev, page }) })}
            />
          )}
        </section>
      </div>
    </>
  );
}

function SortBar({
  search,
  setSearch,
  total,
}: {
  search: DashboardSearch;
  setSearch: (patch: Partial<DashboardSearch>) => void;
  total: number | undefined;
}) {
  const id = useId();
  return (
    <div className="flex flex-wrap items-end justify-between gap-2">
      <p className="text-sm text-muted" aria-live="polite">
        {total !== undefined ? `${total} ${total === 1 ? 'ticket' : 'tickets'}` : ''}
      </p>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-sort`} className="text-xs text-muted">
          Sort
        </Label>
        <NativeSelect
          id={`${id}-sort`}
          className="w-40"
          value={search.sort ?? 'updatedAt'}
          onChange={(e) => setSearch({ sort: e.target.value as DashboardSearch['sort'], order: undefined })}
        >
          {Object.entries(SORT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </NativeSelect>
      </div>
    </div>
  );
}

/**
 * People to filter assignees and responsible owners by: the assignable list, every Editor and Admin. It's the
 * same for every app (poc CT-008), so it's read through the first app the person can assign in.
 */
function useAssignablePeople(): PrincipalSummary[] {
  const session = useSession();
  const appId = session.apps.find((a) => canApp(session, a.appId, 'ticket.assign'))?.appId;
  const people = useQuery({ ...assignableQuery(appId ?? ''), enabled: appId !== undefined });
  return [...(people.data ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function DashboardFilters({
  search,
  setSearch,
  activeCount,
}: {
  search: DashboardSearch;
  setSearch: (patch: Partial<DashboardSearch>) => void;
  activeCount: number;
}) {
  const id = useId();
  const session = useSession();
  const reference = useQuery(referenceQuery);
  // Stage filters keep the bug workflow's stage names (they cover both workflows by stage code).
  const { stages } = useAllStatuses();
  const statusGroups = useStatusGroups();
  const people = useAssignablePeople();
  const appViews = useQueries({ queries: session.apps.map((a) => appQuery(a.appId)) });
  const environments = new Map<string, string>();
  for (const view of appViews) {
    for (const e of view.data?.environments ?? []) {
      environments.set(e.code, e.name);
    }
  }
  const clear = () =>
    setSearch({
      appId: undefined,
      stage: undefined,
      status: undefined,
      assigneeId: undefined,
      assigneeKind: undefined,
      responsibleId: undefined,
      type: undefined,
      priority: undefined,
      environment: undefined,
    });
  return (
    <FilterPanel activeCount={activeCount} onClear={clear}>
      <FilterSection title="App">
        {session.apps.map((a) => (
          <FilterCheckbox
            key={a.appId}
            id={`${id}-app-${a.appId}`}
            label={a.name}
            checked={search.appId?.includes(a.appId) ?? false}
            onCheckedChange={(on) => setSearch({ appId: toggle(search.appId, a.appId, on) })}
          />
        ))}
      </FilterSection>
      <FilterSection title="Stage">
        {stages.map((stage) => (
          <FilterCheckbox
            key={stage.code}
            id={`${id}-stage-${stage.code}`}
            label={stage.name}
            checked={search.stage?.includes(stage.code) ?? false}
            onCheckedChange={(on) => setSearch({ stage: toggle(search.stage, stage.code, on) })}
          />
        ))}
      </FilterSection>
      <FilterSection title="Status">
        <Label htmlFor={`${id}-status`} className="sr-only">
          Status
        </Label>
        <NativeSelect
          id={`${id}-status`}
          value={search.status ?? ''}
          onChange={(e) => setSearch({ status: e.target.value || undefined })}
        >
          <StatusOptions groups={statusGroups} />
        </NativeSelect>
      </FilterSection>
      <FilterSection title="Type">
        {(reference.data?.ticketTypes ?? []).map((t) => (
          <FilterCheckbox
            key={t.code}
            id={`${id}-type-${t.code}`}
            label={t.name}
            checked={search.type?.includes(t.code) ?? false}
            onCheckedChange={(on) => setSearch({ type: toggle(search.type, t.code, on) })}
          />
        ))}
      </FilterSection>
      <FilterSection title="Kind of assignee">
        <Label htmlFor={`${id}-kind`} className="sr-only">
          Kind of assignee
        </Label>
        <NativeSelect
          id={`${id}-kind`}
          value={search.assigneeKind ?? ''}
          onChange={(e) =>
            setSearch({ assigneeKind: (e.target.value || undefined) as DashboardSearch['assigneeKind'] })
          }
        >
          <option value="">Any</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {KIND_LABELS[k]}
            </option>
          ))}
        </NativeSelect>
      </FilterSection>
      <FilterSection title="Assignee">
        <Label htmlFor={`${id}-assignee`} className="sr-only">
          Assignee
        </Label>
        <NativeSelect
          id={`${id}-assignee`}
          value={search.assigneeId ?? ''}
          onChange={(e) => setSearch({ assigneeId: e.target.value || undefined })}
        >
          <option value="">Anyone</option>
          {people.map((p) => (
            <option key={p.principalId} value={p.principalId}>
              {p.displayName}
            </option>
          ))}
        </NativeSelect>
      </FilterSection>
      <FilterSection title="Responsible owner">
        <Label htmlFor={`${id}-responsible`} className="sr-only">
          Responsible owner
        </Label>
        <NativeSelect
          id={`${id}-responsible`}
          value={search.responsibleId ?? ''}
          onChange={(e) => setSearch({ responsibleId: e.target.value || undefined })}
        >
          <option value="">Anyone</option>
          {people
            .filter((p) => p.kind === 'person')
            .map((p) => (
              <option key={p.principalId} value={p.principalId}>
                {p.displayName}
              </option>
            ))}
        </NativeSelect>
      </FilterSection>
      <FilterSection title="Priority">
        {(reference.data?.priorities ?? []).map((p) => (
          <FilterCheckbox
            key={p.code}
            id={`${id}-priority-${p.code}`}
            label={p.label}
            checked={search.priority?.includes(p.code) ?? false}
            onCheckedChange={(on) => setSearch({ priority: toggle(search.priority, p.code, on) })}
          />
        ))}
      </FilterSection>
      <FilterSection title="Environment">
        {[...environments].map(([code, name]) => (
          <FilterCheckbox
            key={code}
            id={`${id}-env-${code}`}
            label={name}
            checked={search.environment?.includes(code) ?? false}
            onCheckedChange={(on) => setSearch({ environment: toggle(search.environment, code, on) })}
          />
        ))}
      </FilterSection>
    </FilterPanel>
  );
}

function Warning({ label }: { label: string }) {
  return (
    <span className="font-bold text-danger" title={label}>
      !<span className="sr-only"> {label}</span>
    </span>
  );
}

function ResponsibleCell({ row }: { row: DashboardRow }) {
  return (
    <span className="inline-flex items-center gap-1">
      <PrincipalName principal={row.responsible} />
      {row.warnings.platformAdminsResponsible ? (
        <Warning label="The Platform admins are responsible: the app has no active fallback owner" />
      ) : null}
    </span>
  );
}

type Open = { kind: 'assign' | 'responsible'; row: DashboardRow } | null;

function RowActions({ row, onOpen }: { row: DashboardRow; onOpen: (open: Open) => void }) {
  const session = useSession();
  const command = useTicketCommand({ channel: 'dashboard' });
  const canAssign = canApp(session, row.app.id, 'ticket.assign');
  const assignable = canAssign && ASSIGNABLE_STAGES.has(row.stage.code) && !isManagedInLinear(row);
  const assigneeId = row.assignee?.principalId ?? null;
  const toMe = useAssignToMe({ ref: row.reference, revision: row.revision, appId: row.app.id }, assigneeId, {
    enabled: assignable,
    channel: 'dashboard',
  });
  if (!canAssign) {
    return null;
  }
  if (assignable) {
    // In progress, or the legacy Assigned status (in Ready), with someone on it.
    const canUnassign =
      assigneeId !== null && (row.status.systemCode === 'assigned' || row.stage.code === 'in_progress');
    return (
      <span className="flex flex-wrap gap-1">
        {toMe.offered ? (
          <Button variant="secondary" size="sm" disabled={toMe.pending} onClick={toMe.assignToMe}>
            Assign to me
          </Button>
        ) : null}
        <Button variant="secondary" size="sm" onClick={() => onOpen({ kind: 'assign', row })}>
          {row.assignee ? 'Reassign' : 'Assign'}
        </Button>
        {canUnassign ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={command.isPending}
            onClick={() =>
              command.mutate({
                ref: row.reference,
                body: { type: 'unassign', expectedRevision: row.revision },
                done: `${row.reference} unassigned`,
              })
            }
          >
            Unassign
          </Button>
        ) : null}
      </span>
    );
  }
  if (OPEN_STAGES.has(row.stage.code) && canApp(session, row.app.id, 'ticket.override_ai')) {
    return (
      <Button variant="secondary" size="sm" onClick={() => onOpen({ kind: 'responsible', row })}>
        Change responsible owner
      </Button>
    );
  }
  return null;
}

function DashboardTable({
  page,
  isFetching,
  onPageChange,
}: {
  page: DashboardPage;
  isFetching: boolean;
  onPageChange: (p: number) => void;
}) {
  const [open, setOpen] = useState<Open>(null);
  const navigate = Route.useNavigate();
  const target: TicketTarget | null = open
    ? { ref: open.row.reference, revision: open.row.revision, appId: open.row.app.id }
    : null;
  const close = (value: boolean) => (value ? null : setOpen(null));
  const showLinear = page.items.some((row) => row.linear !== null);
  // Two lines per row, as root 6.8 draws it: what and who on top, responsibility and timing below.
  const columns: DataColumn<DashboardRow>[] = [
    {
      id: 'ref',
      header: 'App / ref',
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="text-xs text-muted">{row.original.app.name}</span>
          <Link
            to="/tickets/$ref"
            params={{ ref: row.original.reference }}
            className="font-medium whitespace-nowrap text-primary hover:underline"
          >
            {row.original.reference}
          </Link>
        </span>
      ),
    },
    {
      id: 'title',
      header: 'Title / stage',
      cell: ({ row }) => (
        <span className="flex min-w-40 flex-col items-start gap-1">
          <span>{row.original.title}</span>
          <StageBadge stage={row.original.stage} status={row.original.status} />
        </span>
      ),
    },
    ...(showLinear
      ? [
          {
            id: 'linear',
            header: 'Linear',
            cell: ({ row }) => <LinearCell linear={row.original.linear} />,
          } satisfies DataColumn<DashboardRow>,
        ]
      : []),
    { id: 'type', header: 'Type', cell: ({ row }) => <TypeBadge type={row.original.type} /> },
    {
      id: 'people',
      header: 'Assignee (kind) / responsible',
      cell: ({ row }) => (
        <span className="flex flex-col gap-1">
          <PrincipalName principal={row.original.assignee} showKind />
          <span className="text-xs text-muted">
            Responsible: <ResponsibleCell row={row.original} />
          </span>
        </span>
      ),
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: ({ row }) => <RelativeTime iso={row.original.updatedAt} className="whitespace-nowrap text-muted" />,
    },
    {
      id: 'priority',
      header: 'Pri / env',
      cell: ({ row }) => (
        <span className="flex flex-col gap-1">
          <PriorityBadge priority={row.original.priority} />
          <span className="text-xs text-muted">{row.original.environment.name}</span>
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => <RowActions row={row.original} onOpen={setOpen} />,
    },
  ];
  return (
    <>
      <DataTable
        caption="Dashboard tickets"
        columns={columns}
        data={page.items}
        getRowId={(row) => row.id}
        isFetching={isFetching}
        onRowClick={(row) => void navigate({ to: '/tickets/$ref', params: { ref: row.reference } })}
        paging={{ page: page.page, pageSize: page.pageSize, total: page.total, onPageChange }}
        renderCard={(row) => (
          <div className="flex flex-col gap-2 text-sm" data-testid="dashboard-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className="text-muted">{row.app.name} · </span>
                <Link to="/tickets/$ref" params={{ ref: row.reference }} className="font-medium text-primary">
                  {row.reference}
                </Link>
              </span>
              <StageBadge stage={row.stage} status={row.status} />
            </div>
            <p>{row.title}</p>
            {row.linear ? <LinearCell linear={row.linear} /> : null}
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <TypeBadge type={row.type} />
              <PrincipalName principal={row.assignee} showKind />
              <ResponsibleCell row={row} />
              <PriorityBadge priority={row.priority} />
              <span className="text-muted">{row.environment.name}</span>
            </div>
            <p className="text-muted">
              Updated <RelativeTime iso={row.updatedAt} />
            </p>
            <RowActions row={row} onOpen={setOpen} />
          </div>
        )}
      />
      {target && open ? (
        <>
          <AssignDialog
            target={target}
            channel="dashboard"
            open={open.kind === 'assign'}
            onOpenChange={close}
            currentAssigneeId={open.row.assignee?.principalId ?? null}
          />
          <ResponsibleDialog
            target={target}
            channel="dashboard"
            open={open.kind === 'responsible'}
            onOpenChange={close}
            currentId={open.row.responsible?.principalId ?? null}
          />
        </>
      ) : null}
    </>
  );
}
